import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, FileText, PenLine } from 'lucide-react';
import { getEcosystemItems } from '@/data/ecosystem';
import { getCaseStudies } from '@/data/caseStudies';
import { getLive, useLiveTick } from '@/lib/liveContent';
import { trackEvent } from '@/lib/analytics';
import { SectionHeader } from '../SectionHeader';

// Section — related content. CMS-driven slug lists resolved against live
// published content; each group hides itself when empty (nothing invented).
export const CompassRelated = ({ sp }) => {
  useLiveTick();

  const systems = (sp.relatedListings || [])
    .map((slug) => getEcosystemItems().find((it) => it.slug === slug))
    .filter((it) => it && it.slug !== 'amden-compass' && it.status === 'published');

  const caseStudies = (sp.relatedCaseStudies || [])
    .map((slug) => getCaseStudies().find((c) => c.slug === slug))
    .filter((c) => c && c.status === 'published');

  const articles = (sp.relatedInsights || [])
    .map((slug) => (getLive('articles') || []).find((a) => a.slug === slug))
    .filter((a) => a && a.status === 'published');

  if (!systems.length && !caseStudies.length && !articles.length) return null;

  const cardCls =
    'group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900';

  return (
    <section aria-labelledby="compass-related-heading" data-testid="compass-related" className="bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          align="left"
          eyebrow="Keep Exploring"
          title="Related to Amden Compass"
          testid="compass-related-header"
        />

        {systems.length > 0 && (
          <div className="mt-10">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Related Amden systems</h3>
            <ul className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {systems.map((item) => (
                <li key={item.slug} className="h-full">
                  <Link to={item.href || `/ecosystem/services/${item.slug}`} className={cardCls} data-testid={`compass-related-system-${item.slug}`}>
                    <Cpu className="h-5 w-5 text-gold-700" aria-hidden="true" />
                    <p className="mt-3 text-base font-extrabold tracking-tight text-navy-900">{item.name}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{item.shortDescription}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-navy-900">
                      Explore <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {caseStudies.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Relevant case studies</h3>
            <ul className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {caseStudies.map((cs) => (
                <li key={cs.slug} className="h-full">
                  <Link
                    to={`/case-studies/${cs.slug}`}
                    className={cardCls}
                    data-testid={`compass-related-case-${cs.slug}`}
                    onClick={() => trackEvent('compass_related_case_study')}
                  >
                    <FileText className="h-5 w-5 text-gold-700" aria-hidden="true" />
                    <p className="mt-3 text-base font-extrabold tracking-tight text-navy-900">{cs.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{cs.summary}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {articles.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Relevant insights</h3>
            <ul className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map((a) => (
                <li key={a.slug} className="h-full">
                  <Link
                    to={`/insights/${a.slug}`}
                    className={cardCls}
                    data-testid={`compass-related-insight-${a.slug}`}
                    onClick={() => trackEvent('compass_related_insight')}
                  >
                    <PenLine className="h-5 w-5 text-gold-700" aria-hidden="true" />
                    <p className="mt-3 text-base font-extrabold tracking-tight text-navy-900">{a.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};
